import type { TranslationKey } from 'intl/types/translationKey';
import type { GridColDef, GridInitialState } from '@mui/x-data-grid-pro';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';

export enum EntityState {
  published = 'published',
  archived = 'archived',
}

export function isEntityStatePublished(
  state: EntityState
): state is EntityState.published {
  return state === EntityState.published;
}

export function isEntityStateArchived(
  state: EntityState
): state is EntityState.archived {
  return state === EntityState.archived;
}

export function getNextEntityState(current: EntityState): EntityState {
  return isEntityStatePublished(current)
    ? EntityState.archived
    : EntityState.published;
}

export type DocumentState = EntityState;
export const DocumentState = EntityState;

export type IssueState = EntityState;
export const IssueState = EntityState;

export type InspectionState = EntityState;
export const InspectionState = EntityState;

export type InspectionTemplateState = EntityState;
export const InspectionTemplateState = EntityState;

export enum GridColumnFieldBase {
  createdAt = 'createdAt',
  createdBy = 'createdBy',
  modifiedAt = 'modifiedAt',
  modifiedBy = 'modifiedBy',
}

export enum GridColumnFieldDocumentation {
  name = 'name',
  number = 'number',
  currentVersion = 'currentVersion',
  parentDirectory = 'parentDirectory',
  exportedFromIssue = 'exportedFromIssue',
  relatedIssuesCount = 'relatedIssuesCount',
  sites = 'sites',
  levels = 'levels',
  state = 'state',
}

export enum GridColumnFieldInspection {
  orderNumber = 'orderNumber',
  processCode = 'processCode',
  process = 'process',
  inspectionNumber = 'inspectionNumber',
  summary = 'summary',
  status = 'status',
  state = 'state',
  executionDate = 'executionDate',
  controlledParty = 'controlledParty',
  workTypes = 'workTypes',
  author = 'author',
  inspectionResult = 'inspectionResult',
}

export enum GridColumnFieldContract {
  label = 'label',
  parties = 'parties',
}

export enum GridColumnFieldUser {
  label = 'label',
  email = 'email',
  phone = 'phone',
  role = 'role',
  sites = 'sites',
  processes = 'processes',
}

export enum GridColumnFieldCompany {
  shortLabel = 'shortLabel',
  longLabel = 'longLabel',
  finalCostSettled = 'finalCostSettled',
  finalCostOutstanding = 'finalCostOutstanding',
  estimatedCost = 'estimatedCost',
  expectedFine = 'expectedFine',
  imposedFine = 'imposedFine',
  projectInvolvementType = 'projectInvolvementType',
  taxNumber = 'taxNumber',
  correspondenceAddressStreetAndNumber = 'correspondenceAddressStreetAndNumber',
  correspondenceAddressPostalCode = 'correspondenceAddressPostalCode',
  correspondenceAddressCountry = 'correspondenceAddressCountry',
  registeredAddressStreetAndNumber = 'registeredAddressStreetAndNumber',
  registeredAddressPostalCode = 'registeredAddressPostalCode',
  registeredAddressCountry = 'registeredAddressCountry',
}

const GridColumnFieldIssueExtra = {
  state: 'state',
  orderNumber: 'orderNumber',
  process: 'process',
  sites: 'sites',
  levels: 'levels',
  inspectionNumber: 'inspectionNumber',
  workflow: 'workflow',
  lastDecision: 'lastDecision',
} as const;

const GridColumnFieldIssueFromFieldNames = Object.fromEntries(
  Object.values(IssueFieldNames).map((fieldName) => [fieldName, fieldName])
) as Record<IssueFieldNames, IssueFieldNames>;

export const GridColumnFieldIssue = {
  ...GridColumnFieldIssueExtra,
  ...GridColumnFieldIssueFromFieldNames,
} as const;

export type GridColumnFieldIssue =
  (typeof GridColumnFieldIssue)[keyof typeof GridColumnFieldIssue];

export type GridColumnField =
  | GridColumnFieldBase
  | GridColumnFieldDocumentation
  | GridColumnFieldInspection
  | GridColumnFieldContract
  | GridColumnFieldUser
  | GridColumnFieldCompany
  | GridColumnFieldIssue;

export const GridColumnField = {
  ...GridColumnFieldBase,
  ...GridColumnFieldDocumentation,
  ...GridColumnFieldInspection,
  ...GridColumnFieldContract,
  ...GridColumnFieldUser,
  ...GridColumnFieldCompany,
  ...GridColumnFieldIssue,
} as const;

export enum GridColumnWidth {
  small = 50,
  medium = 100,
  mediumLarge = 180,
  large = 200,
  extraLarge = 300,
}

export type GridColumnShape = Omit<
  GridColDef,
  'headerName' | 'sortable'
> & {
  headerNameId: TranslationKey;
  valueOptions?: any;
  getOptionLabel?: any;
  getOptionValue?: any;
};

export enum DataGridStorageKey {
  documentationDataGridColumnsSettings = 'documentation_data_grid_columns_settings',
}

export const DEFAULT_DOCUMENTATION_COLUMNS_ORDER = [
  'name',
  'number',
  'currentVersion',
  'createdBy',
  'createdAt',
  'modifiedBy',
  'modifiedAt',
  'exportedFromIssue',
] as string[];

export const FIXED_STATE_SELECTION_ORDER = [
  DocumentState.published,
  DocumentState.archived,
] as const;

export interface VersionedGridState extends GridInitialState {
  _version?: number;
}
